import {useSelector} from 'react-redux';
import {RootState} from '../store';
import {AccountType} from '../types/AccountType';
import {PermissionModule, ReportPermissionModules} from '../types/enums/permission/PermissionModule';
import {PermissionType} from '../types/enums/permission/PermissionType';
import {ReportTypes} from '../types/enums/ReportTypes';
import {UserRole} from '../types/enums/UserRole';
import {UserType} from '../types/UserType';

export function useHasPermission(permissionModule: PermissionModule, permissionType: PermissionType) {
    const rolePermissions = useSelector((state: RootState) => state.login.userData?.rolePermissions);
    const customPermissions = useSelector((state: RootState) => state.login.userData?.customPermissions);
    const allPermissions = rolePermissions ? (customPermissions ? rolePermissions.concat(customPermissions) : rolePermissions) : customPermissions;
    const currentPermissions = allPermissions?.filter(permission => permission.permissionModule === permissionModule);
    if (currentPermissions) {
        if (currentPermissions.some(currentPermission => currentPermission.permissionType === PermissionType.NO_ACCESS)) {
            return false;
        }
        for (const currentPermission of currentPermissions) {
            if (permissionType === PermissionType.READ) {
                switch (currentPermission.permissionType) {
                    case PermissionType.ALL_READ_AUTH_WRITE:
                    case PermissionType.WRITE:
                    case PermissionType.AUTHORIZED_WRITE:
                    case PermissionType.READ:
                    case PermissionType.AUTHORIZED_READ:
                        return true;
                    case PermissionType.NO_ACCESS:
                    default:
                        break;
                }
            }
            if (permissionType === PermissionType.WRITE) {
                switch (currentPermission.permissionType) {
                    case PermissionType.ALL_READ_AUTH_WRITE:
                    case PermissionType.WRITE:
                    case PermissionType.AUTHORIZED_WRITE:
                        return true;
                    case PermissionType.NO_ACCESS:
                    default:
                        break;
                }
            }
        }
    }
    return false;
}

export function useCanReadAnyReport() {
    const rolePermissions = useSelector((state: RootState) => state.login.userData?.rolePermissions);
    const customPermissions = useSelector((state: RootState) => state.login.userData?.customPermissions);
    const allPermissions = [rolePermissions, customPermissions];
    for (const element of allPermissions) {
        const currentPermissions = element?.filter(cp => cp.permissionModule && ReportPermissionModules.includes(cp.permissionModule));
        if (currentPermissions) {
            for (const currentPermission of currentPermissions) {
                switch (currentPermission.permissionType) {
                    case PermissionType.ALL_READ_AUTH_WRITE:
                    case PermissionType.WRITE:
                    case PermissionType.AUTHORIZED_WRITE:
                    case PermissionType.READ:
                    case PermissionType.AUTHORIZED_READ:
                        return true;
                    case PermissionType.NO_ACCESS:
                    default:
                        break;
                }
            }
        }
    }
    return false;
}

export function getPermissionModuleForReportType(reportType: ReportTypes) {
    switch (reportType) {
        case ReportTypes.CONTRIBUTION:
            return PermissionModule.CONTRIBUTION_REPORT;
        case ReportTypes.PM:
            return PermissionModule.PM_REPORT;
        case ReportTypes.DL :
            return PermissionModule.DL_REPORT;
        case ReportTypes.BROADCASTING_VAN:
            return PermissionModule.DL_REPORT;
        case ReportTypes.DIRECTOR:
            return PermissionModule.DIRECTOR_REPORT;
        case ReportTypes.SIGNAL_EVALUATION:
            return PermissionModule.SIGNAL_EVALUATION;
        case ReportTypes.DFB_CONTRIBUTION:
            return PermissionModule.DFB_CONTRIBUTION;
    }
}

export function hasPermission(account: AccountType | UserType | undefined, permissionModule: PermissionModule, permissionType: PermissionType) {
    if (!account) {
        return false;
    }
    const rolePermissions = account.rolePermissions;
    const customPermissions = account.customPermissions;
    const allPermissions = [rolePermissions, customPermissions];
    for (const element of allPermissions) {
        const currentPermissions = element?.filter(cp => cp.permissionModule === permissionModule);
        if (currentPermissions) {
            for (const currentPermission of currentPermissions) {
                if (permissionType === PermissionType.READ) {
                    switch (currentPermission.permissionType) {
                        case PermissionType.ALL_READ_AUTH_WRITE:
                        case PermissionType.WRITE:
                        case PermissionType.AUTHORIZED_WRITE:
                        case PermissionType.READ:
                        case PermissionType.AUTHORIZED_READ:
                            return true;
                        default:
                            break;
                    }
                }
                if (permissionType === PermissionType.WRITE) {
                    switch (currentPermission.permissionType) {
                        case PermissionType.ALL_READ_AUTH_WRITE:
                        case PermissionType.WRITE:
                        case PermissionType.AUTHORIZED_WRITE:
                            return true;
                        default:
                            break;
                    }
                }
            }
        }
    }
    return false;
}

export function useIsAdminOrQMOrReportResponsible() {
    const userData = useSelector((state: RootState) => state.login.userData);
    if (!userData) {
        return false;
    }
    const isAdmin = userData.role === UserRole.ADMIN;
    const isQMResponsible = userData.role === UserRole.QM_RESPONSIBLE;
    const isReportResponsible = userData.isResponsibleUser;
    return isAdmin || isQMResponsible || isReportResponsible;
}